import { useState, useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getAllLevel } from "../../../api/pedagogicElement";
import { getStudentArchivesById } from "../../../api/archives";
import { getAllAcademicYear } from "../../../api/generalParam";
import {
  getExternalNoteById,
  updateExternalNote,
} from "../../../api/externalNote";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { getStudentById } from "../../../api/etudiants";
import {
  createFileAttachement,
  getFileAttachementByEntityId,
} from "../../../api/fileAttachement";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  id_parcoursStudent: any;
  externalNoteID: UUID;
  externalNotes: any;
  studentArchive: any;
  onClose: () => void;
  onSaved?: () => void;
};

interface Year {
  id: string;
  label: string;
  status: string;
}

interface Level {
  id: string;
  label: string;
  abreviation: string;
}

const EditExternalNoteModal = ({
  externalNoteID,
  studentArchive,
  externalNotes,
  id_parcoursStudent,
  show,
  onClose,
  onSaved,
}: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();
  const [allLevels, setAllLevels] = useState<Level[]>([]);
  const [years, setYears] = useState<Year[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [student, setStudent] = useState<{
    id: string;
    value: string;
    label: any;
  }>();

  const [existingFile, setExistingFile] = useState<{
    name: string;
    url: string;
  } | null>(null);

  //Schéma de notre formulaire
  const createSchema = z.object({
    university: z.string().nonempty("L'établissement est requis"),
    classe: z.string().nonempty("La classe est requise"),
    average: z
      .number({
        invalid_type_error:
          "Doit être un nombre (utilisez , ou . pour les décimales)",
      })
      .min(0, "Minimum 0")
      .max(20, "Maximum 20"),
    id_studentProfil: z.string().nonempty("L'étudiant est requis"),
    academicYear: z.string().nonempty("L'année académique est requise"),
    id_level: z.string().nonempty("Le niveau est requis"),
    matricule: z.string().nonempty("Le matricule est requis"),
    pieceJustificatif: z
      .any()
      .refine(
        (file) => !file || file instanceof File,
        "Le fichier doit être un fichier valide",
      )
      .refine(
        (file) => !file || file.size > 0,
        "Le fichier ne doit pas être vide",
      )
      .refine((file) => !file || file.size <= 5_000_000, "Taille max: 5MB")
      .refine(
        (file) =>
          !file ||
          ["application/pdf", "image/jpeg", "image/png"].includes(file.type),
        "Formats acceptés: PDF, JPEG, PNG",
      ),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data: any) => {
    try {
      const formData = new FormData();

      // Ajout des champs texte
      Object.keys(data).forEach((key) => {
        if (key !== "pieceJustificatif") {
          formData.append(key, data[key]);
        }
      });

      // Gestion du fichier
      if (
        data.pieceJustificatif instanceof File &&
        data.pieceJustificatif.size > 0
      ) {
        formData.append("entity_id", externalNoteID);
        formData.append("entity_type", "ExternalNote");
        formData.append("pieceJustificatif", data.pieceJustificatif);
        await createFileAttachement(
          formData,
          buildPermissionContext(Resource.REGISTRATION, Action.CREATE),
        );
      }

      //Envoi des datas pour création vers le backend
      await updateExternalNote(
        { ...data, id_parcoursStudent: id_parcoursStudent },
        externalNoteID,
      );

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Matière ajoutée !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      // console.log(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFiles([file]);
      setValue("pieceJustificatif", file, { shouldValidate: true });
    } else {
      setSelectedFiles([]);
      setValue("pieceJustificatif", new File([], ""), { shouldValidate: true });
    }
  };

  // 👉 Change handler pour le select des Année Académique
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setValue("academicYear", selectedId, { shouldValidate: true });
  };

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id || !show) return;

    if (!show || !externalNoteID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchNote = async () => {
      try {
        // Récupérer les années
        const yearsRes = await getAllAcademicYear();
        setYears(yearsRes.data);

        // Récupérer la note existante
        const noteRes = await getExternalNoteById(externalNoteID);

        const response = await getFileAttachementByEntityId(
          noteRes?.data?.id,
          "ExternalNote",
          buildPermissionContext(Resource.REGISTRATION, Action.READ),
        );
        // console.log("File External : ", response);

        const attachment =
          Array.isArray(response.data) && response.data.length > 0
            ? response.data[0]
            : response.data;

        if (attachment) {
          setExistingFile({
            name: attachment?.filename || "Fichier joint",
            url: attachment?.temporaryUrl,
          });
        }

        // Trouver l'année académique correspondante
        const foundYear = yearsRes.data.find(
          (y: any) =>
            y.id.toString() === noteRes.data.id_academicYear.toString(),
        );
        if (!foundYear) {
          toast.warning("L'année académique de cette note est introuvable.");
        }

        // Charger les niveaux pour cette année
        const levelsRes = await getAllLevel(
          noteRes.data.id_academicYear,
          buildPermissionContext(Resource.EXTERNAL_NOTE, Action.UPDATE),
        );
        setAllLevels(levelsRes.data || []);

        // Remplir le formulaire après que les niveaux soient disponibles
        reset({
          academicYear: noteRes.data.id_academicYear,
          id_studentProfil: noteRes.data.id_studentProfil,
          id_level: noteRes.data.id_level, // ✅ le select sera rempli
          university: noteRes.data.university,
          matricule: noteRes.data.matricule,
          classe: noteRes.data.classe,
          average: noteRes.data.average,
          pieceJustificatif: null,
        });

        // Charger l'étudiant
        const studentsRes = await getStudentById(
          noteRes.data.id_studentProfil,
          buildPermissionContext(Resource.EXTERNAL_NOTE, Action.UPDATE),
        );
        const studentArchive = await getStudentArchivesById(
          studentsRes.data.id,
          buildPermissionContext(Resource.EXTERNAL_NOTE, Action.UPDATE),
        );
        const studentArchData = studentArchive.data.find(
          (a: any) => a.id_academicYear === selectedYear?.id,
        );
        setStudent({
          id: studentsRes.data.id,
          value: studentsRes.data.id,
          label: `${studentArchData?.Class?.Level?.abbreviation || ""} - ${
            studentArchData?.Class?.label || ""
          } : ${studentsRes.data.firstname} ${studentsRes.data.lastname}`,
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
          error.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Erreur lors du chargement de la note",
        );
      }
    };

    fetchNote();
  }, [show]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier une note externe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Étudiant</label>
                  <br />
                  {student ? <span>{student.label}</span> : null}

                  {errors.id_studentProfil && (
                    <div className="invalid-feedback">
                      {errors.id_studentProfil.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Année Universitaire</label>
                  <select
                    {...register("academicYear")}
                    aria-label="Default select example"
                    onChange={handleYearChange}
                    className={
                      errors.academicYear
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {years &&
                      years
                        .filter((y) => {
                          // Exclure si une archive existe pour cette année
                          const hasArchive = studentArchive.some(
                            (archiveItem: any) =>
                              archiveItem.id_academicYear === y.id,
                          );
                          // Exclure si une autre note externe existe pour cette année
                          const hasOtherExternalNote = externalNotes.some(
                            (exterNoteItem: any) =>
                              exterNoteItem.id_academicYear === y.id &&
                              exterNoteItem.id !== externalNoteID,
                          );
                          return !hasArchive && !hasOtherExternalNote;
                        })
                        .map((year) => (
                          <option key={year.id} value={year.id}>
                            Année {year.label}
                          </option>
                        ))}
                  </select>
                  {errors.academicYear && (
                    <div className="invalid-feedback">
                      {errors.academicYear.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Niveau d'Étude</label>
                  <select
                    {...register("id_level")}
                    aria-label="Default select example"
                    className={
                      errors.id_level ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {allLevels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  {errors.id_level && (
                    <div className="invalid-feedback">
                      {errors.id_level.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-8 col-xl-8 col-md-8">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Établissement
                  </label>
                  <input
                    {...register("university")}
                    type="text"
                    className={
                      errors.university
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: IAM - ISPRIC"
                  />
                  {errors.university && (
                    <div className="invalid-feedback">
                      {errors.university.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-4 col-xl-4 col-md-4">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    N° Ref
                  </label>
                  <input
                    {...register("matricule")}
                    type="text"
                    className={
                      errors.matricule
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: REF00167"
                  />
                  {errors.matricule && (
                    <div className="invalid-feedback">
                      {errors.matricule.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Classe
                  </label>
                  <input
                    {...register("classe")}
                    type="text"
                    className={
                      errors.classe ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Informatique, Finance"
                  />
                  {errors.classe && (
                    <div className="invalid-feedback">
                      {errors.classe.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Note Obtenue
                  </label>
                  <input
                    {...register("average", {
                      setValueAs: (v) => {
                        if (typeof v === "string") {
                          // remplace la virgule par un point
                          const parsed = parseFloat(v.replace(",", "."));
                          return isNaN(parsed) ? undefined : parsed;
                        }
                        return v;
                      },
                      validate: {
                        validNumber: (v) => !isNaN(v) || "Nombre requis",
                        min: (v) => v >= 0 || "Minimum 0",
                        max: (v) => v <= 20 || "Maximum 20",
                      },
                    })}
                    step="0.01"
                    min="0"
                    max="20"
                    type="number"
                    className={
                      errors.average
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: 12,5 ou 14.75"
                  />
                  {errors.average && (
                    <div className="invalid-feedback">
                      {errors.average.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="profile-upload mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Pièce(s) Justificatif(s)
                  </label>
                  <div className="profile-uploader d-flex align-items-center">
                    {/* Affichage du fichier existant */}
                    {existingFile && !selectedFiles.length && (
                      <div className="mb-3 p-2 border rounded">
                        <a
                          href={`${existingFile.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex align-items-center"
                        >
                          <i className="ti ti-file me-2" />
                          {existingFile.name}
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger"
                          onClick={() => setExistingFile(null)}
                        >
                          Changer de fichier
                        </button>
                      </div>
                    )}

                    {/* Input pour nouveau fichier */}
                    {(!existingFile || selectedFiles.length) && (
                      <>
                        <div className="drag-upload-btn mb-3">
                          Charger un fichier
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileChange}
                            className="form-control image-sign"
                          />
                        </div>
                        {selectedFiles.length > 0 && (
                          <div className="mt-2">
                            <span className="badge bg-light text-dark">
                              {selectedFiles[0].name}
                            </span>
                            <button
                              type="button"
                              className="btn btn-danger mb-3 ms-3"
                              onClick={() => {
                                setSelectedFiles([]);
                                setValue("pieceJustificatif", null);
                              }}
                            >
                              Supprimer
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <p className="fs-12">
                    Taille max : 4MB, Formats : JPG, PNG, PDF, DOCx
                  </p>
                  {errors.pieceJustificatif && (
                    <p className="text-red-500 text-sm">
                      {/* {errors.pieceJustificatif.message} */}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditExternalNoteModal,
  [buildPermission(Resource.EXTERNAL_NOTE, Action.UPDATE)],
  "masquer",
);
